import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AudioWaveform,
  Activity,
  AlertTriangle,
  Route,
  FlaskConical,
  TrendingUp,
  Rocket,
  RefreshCw,
  FileText,
  Settings,
  Folder,
  ChevronDown,
  LogOut,
  BookOpen,
  Sparkles,
  ArrowRight,
  Menu,
  X,
  MoreVertical,
} from 'lucide-react';
import { Project, Session } from '../types';
import { cn, getSessionStatus } from '../utils';
import {
  useAllSessions,
  useAuth,
  useAutoRolloutQueue,
  useGitHubAuthStatus,
  useIssueStats,
} from '../hooks/useApi';
import ProjectSettingsModal from './ProjectSettingsModal';
import ManageProjectsModal from './ManageProjectsModal';

interface LayoutProps {
  children: React.ReactNode;
  projects: Project[];
  selectedProject: Project | null;
  onProjectChange: (project: Project | null) => void;
  onLogout: () => void;
  onProjectDeleted: () => void;
}

// 30 two-minute buckets covering the last hour.
const WAVEFORM_BUCKETS = 30;
const HOUR_MS = 60 * 60 * 1000;
const BUCKET_MS = HOUR_MS / WAVEFORM_BUCKETS;

function buildWaveform(sessions: Session[]): number[] {
  const now = Date.now();
  const bars = new Array(WAVEFORM_BUCKETS).fill(0);
  for (const s of sessions) {
    const age = now - s.timestamp;
    if (age < 0 || age > HOUR_MS) continue;
    const idx = Math.min(WAVEFORM_BUCKETS - 1, Math.floor((HOUR_MS - age) / BUCKET_MS));
    bars[idx] += 1;
  }
  return bars;
}

function pickLiveNow(sessions: Session[], limit = 3): Session[] {
  // Most recent N sessions started in the last 30 min — looser than "live"
  // but matches what the SDK can actually surface (no real liveness ping).
  const cutoff = Date.now() - 30 * 60 * 1000;
  return sessions
    .filter((s) => s.timestamp >= cutoff)
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, limit);
}

function pathFromUrl(url: string | undefined): string {
  if (!url) return '/';
  try {
    return new URL(url).pathname || '/';
  } catch {
    return url;
  }
}

function elapsedLabel(ts: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

const Layout = ({
  children,
  projects,
  selectedProject,
  onProjectChange,
  onLogout,
  onProjectDeleted,
}: LayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSettingsModal, setProjectSettingsModal] = useState<{
    isOpen: boolean;
    project: Project | null;
  }>({ isOpen: false, project: null });
  const [manageProjectsModal, setManageProjectsModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Pull a small slice of cross-project sessions for the rail's waveform +
  // Live now feed. Cheap query (limit 50) so it stays out of the way.
  const sidebarSessionsQuery = useAllSessions({ limit: 50, sortBy: 'timestamp', sortOrder: 'desc' });
  const sidebarSessions = sidebarSessionsQuery.data?.data || [];
  const waveformBars = useMemo(() => buildWaveform(sidebarSessions), [sidebarSessions]);
  const liveSessions = useMemo(() => pickLiveNow(sidebarSessions), [sidebarSessions]);
  const waveformMax = Math.max(1, ...waveformBars);

  // Real nav-badge counts — Issues comes from per-project issue stats,
  // Auto-Rollout from the project's pending approval queue.
  const issueStatsQuery = useIssueStats(selectedProject?._id ?? '', {
    enabled: !!selectedProject,
  });
  const rolloutQueueQuery = useAutoRolloutQueue(selectedProject?._id ?? '', {
    enabled: !!selectedProject,
  });
  const issueOpenCount = issueStatsQuery.data?.data?.openIssues ?? 0;
  const rolloutPendingCount = rolloutQueueQuery.data?.data?.length ?? 0;

  // GitHub auth status feeds the onboarding nudge's "connect GitHub" step.
  const githubStatusQuery = useGitHubAuthStatus();
  const githubConnected = !!githubStatusQuery.data?.data?.connected;

  // User identity for the sidebar footer — pulled from the live auth session
  // rather than a static fallback.
  const { data: authState } = useAuth();
  const authUser = authState?.session?.user;
  const displayName =
    authUser?.name?.trim() || authUser?.email?.split('@')[0] || 'You';
  const displayEmail = authUser?.email || '';
  const avatarUrl = authUser?.picture;

  // Don't render the onboarding nudge until every underlying check has
  // resolved. Otherwise the card flashes on every refresh: while sessions /
  // GitHub-status queries are still loading they all read as "not done", the
  // nudge mounts, then disappears the instant data lands.
  const onboardingReady =
    !sidebarSessionsQuery.isLoading && !githubStatusQuery.isLoading;

  const onboarding = useMemo(() => {
    const hasProject = projects.length > 0;
    const hasSessions = sidebarSessionsQuery.data
      ? (sidebarSessionsQuery.data.pagination?.total || sidebarSessions.length) > 0
      : false;
    const steps = [
      { key: 'project', label: 'Create a project', done: hasProject },
      { key: 'sdk', label: 'Install the SDK', done: hasSessions },
      { key: 'github', label: 'Connect GitHub', done: githubConnected },
    ];
    const done = steps.filter((s) => s.done).length;
    const next = onboardingReady ? steps.find((s) => !s.done) : undefined;
    return { steps, done, total: steps.length, next };
  }, [
    projects.length,
    sidebarSessions.length,
    sidebarSessionsQuery.data,
    githubConnected,
    onboardingReady,
  ]);

  const navigation: Array<{
    name: string;
    href: string;
    icon: typeof Activity;
    badge?: { count: number; tone: 'red' | 'primary' };
  }> = [
    { name: 'Sessions', href: '/sessions', icon: Activity },
    {
      name: 'Issues',
      href: '/issues',
      icon: AlertTriangle,
      ...(issueOpenCount > 0 && { badge: { count: issueOpenCount, tone: 'red' as const } }),
    },
    { name: 'Journeys', href: '/journeys', icon: Route },
    { name: 'Experiments', href: '/experiments', icon: FlaskConical },
    { name: 'Results', href: '/results', icon: TrendingUp },
    {
      name: 'Auto-Rollout',
      href: '/auto-rollout',
      icon: Rocket,
      ...(rolloutPendingCount > 0 && {
        badge: { count: rolloutPendingCount, tone: 'primary' as const },
      }),
    },
    { name: 'Feedback', href: '/feedback', icon: RefreshCw },
    { name: 'Reports', href: '/reports', icon: FileText },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const isActive = (href: string) => {
    const path = location.pathname;
    if (href === '/sessions') {
      return (
        path === '/' ||
        path === '/sessions' ||
        path.startsWith('/sessions/') ||
        path === '/dashboard' ||
        path === '/all-sessions'
      );
    }
    if (href === '/issues') {
      return path === '/issues' || path === '/review';
    }
    return path === href;
  };

  const renderNavItems = (onClick?: () => void) =>
    navigation.map((item) => {
      const active = isActive(item.href);
      const Icon = item.icon;
      return (
        <Link
          key={item.name}
          to={item.href}
          onClick={onClick}
          className={cn(
            'flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[13px] font-medium transition-colors',
            active
              ? 'bg-primary-50 text-primary-700'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="flex-1">{item.name}</span>
          {item.badge && (
            <span
              className={cn(
                'px-1.5 py-px rounded text-[10px] font-semibold tabular-nums',
                item.badge.tone === 'red'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-primary-100 text-primary-700'
              )}
            >
              {item.badge.count}
            </span>
          )}
        </Link>
      );
    });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar — simplified: brand + nav only */}
      <div
        className={cn(
          'fixed inset-0 z-50 lg:hidden',
          sidebarOpen ? 'block' : 'hidden'
        )}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-[244px] flex-col bg-white">
          <div className="flex h-14 items-center justify-between px-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-gray-900 text-white flex items-center justify-center">
                <AudioWaveform className="h-4 w-4" />
              </div>
              <div className="font-semibold text-gray-900 tracking-tight text-[15px] leading-none">
                Sessions
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-2 space-y-0.5">
            {renderNavItems(() => setSidebarOpen(false))}
          </nav>
          <div className="p-3 border-t border-gray-100">
            <div className="flex items-center gap-2.5 px-1.5 py-1">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="h-7 w-7 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-violet-400 to-rose-400 flex items-center justify-center text-[11px] font-semibold text-white uppercase">
                  {displayName.charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-gray-900 truncate">
                  {displayName}
                </div>
                <div className="text-[11px] text-gray-500 truncate">{displayEmail}</div>
              </div>
              <button
                onClick={onLogout}
                className="text-gray-400 hover:text-gray-700"
                title="Log out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-[244px] lg:flex-col lg:bg-white lg:border-r lg:border-gray-200">
        {/* Brand + waveform header */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-gray-900 text-white flex items-center justify-center">
              <AudioWaveform className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 tracking-tight text-[15px] leading-none">
                Sessions
              </div>
              <div className="text-[10px] text-gray-500 mt-1">last hour</div>
            </div>
          </div>
          <div className="mt-2.5 flex items-end gap-px h-9">
            {waveformBars.map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm"
                style={{
                  height: `${Math.max(2, (h / waveformMax) * 100)}%`,
                  // Last 8 minutes (4 buckets) light up amber; rest is gray-200.
                  background: i >= WAVEFORM_BUCKETS - 4 ? 'rgb(217 119 87)' : 'rgb(229 231 235)',
                }}
              />
            ))}
          </div>
          <div className="flex items-center justify-between mt-1.5 text-[10.5px] text-gray-500">
            <span>60m ago</span>
            <span className="flex items-center gap-1">
              {liveSessions.length > 0 ? (
                <>
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                  </span>
                  <span className="text-gray-700 font-medium tabular-nums">
                    {liveSessions.length}
                  </span>{' '}
                  recent
                </>
              ) : (
                <span className="text-gray-400">no recent</span>
              )}
            </span>
          </div>
        </div>

        {/* Nav */}
        <nav className="px-2 py-1 space-y-0.5">{renderNavItems()}</nav>

        {/* Recent sessions mini-feed */}
        {liveSessions.length > 0 && (
          <div className="px-3 mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-1.5 px-1">
              <div className="flex items-center gap-1.5 text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                </span>
                Recent
              </div>
              <span className="text-[10.5px] text-gray-400 tabular-nums">
                {liveSessions.length}
              </span>
            </div>
            <div className="space-y-0.5">
              {liveSessions.map((s) => {
                const status = getSessionStatus(s);
                const path = pathFromUrl(s.metadata?.url);
                return (
                  <Link
                    key={s._id}
                    to={`/sessions/${encodeURIComponent(s.projectId)}/${encodeURIComponent(s.sessionId)}`}
                    className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-gray-50 group"
                  >
                    <span
                      className={cn(
                        'h-1.5 w-1.5 rounded-full shrink-0',
                        status === 'error'
                          ? 'bg-red-500'
                          : status === 'warning'
                            ? 'bg-amber-500'
                            : 'bg-emerald-500'
                      )}
                    />
                    <span className="text-[12px] text-gray-700 font-mono truncate flex-1 group-hover:text-gray-900">
                      {path}
                    </span>
                    <span className="text-[10px] text-gray-400 tabular-nums">
                      {elapsedLabel(s.timestamp)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Onboarding nudge — disappears once everything's done. */}
        {onboarding.next && (
          <div className="mx-3 mt-3 rounded-lg border border-primary-100 bg-primary-50/40 p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-primary-900">
                Get the most out of Sessions
              </span>
              <span className="text-[10px] text-primary-700 tabular-nums font-medium">
                {onboarding.done} / {onboarding.total}
              </span>
            </div>
            <div className="h-1 rounded-full bg-primary-100 overflow-hidden">
              <div
                className="h-full bg-primary-600 rounded-full transition-all"
                style={{ width: `${(onboarding.done / onboarding.total) * 100}%` }}
              />
            </div>
            <button
              type="button"
              onClick={() => navigate(onboarding.next?.key === 'github' ? '/settings' : '/sessions')}
              className="mt-1.5 text-[11px] text-primary-700 hover:text-primary-800 font-medium inline-flex items-center gap-0.5"
            >
              {onboarding.next.label} <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto">
          <div className="px-3 py-2 flex items-center gap-3 text-[11px] text-gray-500 border-t border-gray-100">
            <a
              href="https://github.com/chaseleto/sessions-sdk#readme"
              target="_blank"
              rel="noreferrer"
              className="hover:text-gray-700 inline-flex items-center gap-1"
            >
              <BookOpen className="h-3 w-3" /> Docs
            </a>
            <Link
              to="/feedback"
              className="hover:text-gray-700 inline-flex items-center gap-1"
            >
              <Sparkles className="h-3 w-3" /> Activity
            </Link>
          </div>
          <div className="p-3 border-t border-gray-100">
            <div className="flex items-center gap-2.5 px-1.5 py-1">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="h-7 w-7 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-violet-400 to-rose-400 flex items-center justify-center text-[11px] font-semibold text-white uppercase">
                  {displayName.charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-gray-900 truncate">
                  {displayName}
                </div>
                <div className="text-[11px] text-gray-500 truncate">{displayEmail}</div>
              </div>
              <button
                onClick={onLogout}
                className="text-gray-400 hover:text-gray-700"
                title="Log out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-[244px]">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-4 border-b border-gray-200 bg-white px-4 sm:px-6">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Project chip */}
          <div className="relative flex items-center gap-2 text-[13px]">
            <Folder className="h-4 w-4 text-gray-400" />
            <span className="text-gray-500">{selectedProject ? 'Project' : 'Scope'}</span>
            <button
              onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
              className="flex items-center gap-1 font-medium text-gray-900 hover:text-gray-700"
            >
              {selectedProject?.name || 'All projects'}
              <ChevronDown className="h-4 w-4" />
            </button>

            {projectDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-56 rounded-md bg-white shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scope
                  </div>
                  <div
                    className={cn(
                      'flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-100',
                      !selectedProject ? 'text-primary-700 bg-primary-50' : 'text-gray-700',
                    )}
                  >
                    <button
                      onClick={() => {
                        setProjectDropdownOpen(false);
                        onProjectChange(null);
                        navigate('/sessions?scope=all');
                      }}
                      className="flex-1 text-left"
                    >
                      <div className="font-medium">All projects</div>
                      <div className="text-xs text-gray-500 truncate">
                        Sessions, issues, and feedback across every project
                      </div>
                    </button>
                  </div>
                  <div className="px-3 pt-2 pb-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projects
                  </div>
                  {projects.map((project) => (
                    <div
                      key={project.projectId}
                      className={cn(
                        'flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-100',
                        selectedProject?.projectId === project.projectId
                          ? 'text-primary-700 bg-primary-50'
                          : 'text-gray-700'
                      )}
                    >
                      <button
                        onClick={() => {
                          onProjectChange(project);
                          setProjectDropdownOpen(false);
                          navigate('/sessions');
                        }}
                        className="flex-1 text-left"
                      >
                        <div className="font-medium">{project.name}</div>
                        {project.description && (
                          <div className="text-xs text-gray-500 truncate">
                            {project.description}
                          </div>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setProjectSettingsModal({ isOpen: true, project });
                          setProjectDropdownOpen(false);
                        }}
                        className="ml-2 p-1 hover:bg-gray-200 rounded"
                        title="Project Settings"
                      >
                        <MoreVertical className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <div className="border-t border-gray-200 mt-2 pt-2">
                    <button
                      onClick={() => {
                        setManageProjectsModal(true);
                        setProjectDropdownOpen(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Manage Projects
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right side — connection indicator reflects whether the SDK
              has uploaded a session in the last 30 minutes (matches what
              `pickLiveNow` considers "recent"). */}
          <div className="ml-auto flex items-center gap-3 text-[12px] text-gray-500">
            {liveSessions.length > 0 ? (
              <div
                className="flex items-center gap-1.5"
                title={`${liveSessions.length} session${liveSessions.length === 1 ? '' : 's'} in the last 30 min`}
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                </span>
                Live
              </div>
            ) : (
              <div className="flex items-center gap-1.5" title="No sessions in the last 30 min">
                <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                Idle
              </div>
            )}
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>

      {/* Project Settings Modal */}
      <ProjectSettingsModal
        project={projectSettingsModal.project}
        isOpen={projectSettingsModal.isOpen}
        onClose={() => setProjectSettingsModal({ isOpen: false, project: null })}
        onProjectDeleted={() => {
          onProjectDeleted();
          setProjectSettingsModal({ isOpen: false, project: null });
        }}
      />

      {/* Manage Projects Modal */}
      <ManageProjectsModal
        isOpen={manageProjectsModal}
        onClose={() => setManageProjectsModal(false)}
        onProjectDeleted={onProjectDeleted}
        onProjectCreated={onProjectDeleted}
      />
    </div>
  );
};

export default Layout;
